import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Filter, Loader2 } from "lucide-react";

const RANGES = [7, 30, 90];

function FunnelBar({ stage, max }) {
  const pct = max > 0 ? Math.max(2, (stage.count / max) * 100) : 0;
  const colorByKey = {
    applied: "bg-slate-500",
    invited: "bg-blue-500",
    opened: "bg-sky-500",
    started: "bg-violet-500",
    completed: "bg-indigo-600",
    hired: "bg-emerald-600",
  };
  return (
    <div
      className="flex items-center gap-3 py-2"
      data-testid={`funnel-stage-${stage.key}`}
    >
      <div className="w-32 shrink-0 text-xs font-semibold text-slate-700">
        {stage.label}
      </div>
      <div className="flex-1">
        <div className="h-7 w-full bg-slate-100">
          <div
            className={`flex h-full items-center justify-end pr-2 text-[11px] font-bold text-white ${colorByKey[stage.key] || "bg-slate-400"}`}
            style={{ width: `${pct}%` }}
          >
            {stage.count > 0 && stage.count}
          </div>
        </div>
      </div>
      <div className="w-28 shrink-0 text-right">
        <div className="text-base font-extrabold tabular-nums text-slate-900">
          {stage.count}
        </div>
        {stage.pct_of_top !== null && stage.pct_of_top !== undefined && (
          <div className="text-[10px] uppercase tracking-wider text-slate-400">
            {stage.pct_of_top}% of top
          </div>
        )}
      </div>
    </div>
  );
}

export default function FunnelWidget() {
  const [days, setDays] = useState(30);
  const [jobId, setJobId] = useState("");
  const [jobs, setJobs] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/jobs")
      .then(({ data }) => setJobs(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    const params = { days };
    if (jobId) params.job_id = jobId;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/funnel", { params });
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days, jobId]);

  const max = useMemo(() => {
    if (!data?.stages) return 0;
    return Math.max(...data.stages.map((s) => s.count));
  }, [data]);

  if (!data) return null;
  const empty = data.applied === 0;

  return (
    <div
      data-testid="funnel-widget"
      className="mt-6 border border-slate-200 bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          <Filter className="h-3 w-3" />
          Pipeline funnel
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {jobs.length > 0 && (
            <select
              data-testid="funnel-job-select"
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              className="border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 outline-none focus:border-blue-500"
            >
              <option value="">All roles</option>
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.title}
                </option>
              ))}
            </select>
          )}
          <div className="flex border border-slate-200">
            {RANGES.map((r) => (
              <button
                key={r}
                data-testid={`funnel-range-${r}`}
                onClick={() => setDays(r)}
                className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${
                  days === r
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {r}d
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="px-5 py-4">
        {loading ? (
          <div className="flex h-32 items-center justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : empty ? (
          <div
            data-testid="funnel-empty"
            className="py-6 text-center text-sm text-slate-400"
          >
            No applicants yet in the last {days} days
            {jobId ? " for this role" : ""}.
          </div>
        ) : (
          <>
            <div className="space-y-0.5">
              {data.stages.map((s) => (
                <FunnelBar key={s.key} stage={s} max={max} />
              ))}
            </div>
            <div className="mt-4 grid gap-px bg-slate-100 sm:grid-cols-3">
              <div className="bg-white px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Invite → Started
                </div>
                <div className="mt-1 text-xl font-extrabold tabular-nums text-slate-900">
                  {data.invited > 0
                    ? `${Math.round((data.started / data.invited) * 100)}%`
                    : "—"}
                </div>
              </div>
              <div className="bg-white px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Completed → Hired
                </div>
                <div className="mt-1 text-xl font-extrabold tabular-nums text-slate-900">
                  {data.completed > 0
                    ? `${Math.round((data.hired / data.completed) * 100)}%`
                    : "—"}
                </div>
              </div>
              <div className="bg-white px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Applied → Hired
                </div>
                <div className="mt-1 text-xl font-extrabold tabular-nums text-emerald-700">
                  {data.applied > 0
                    ? `${Math.round((data.hired / data.applied) * 100)}%`
                    : "—"}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
